import { Text } from '@viapeople/components'
import { MarkdownPreview } from '../MarkdownEditor'
import type {
	AggregationCustomProps,
	AggregationOptionData,
	AggregationResponseGroup,
	AggregationRoleData,
	ComponentProps,
} from './types'
import { getLabelVariant } from './utils'

type FormsAggregationProps = ComponentProps & {
	data?: AggregationResponseGroup[]
	optionData?: AggregationOptionData[]
	numericValue?: number
	roles?: AggregationRoleData[]
	loading?: boolean
	emptyPlaceholder?: string
	renderAsHtml?: boolean
}

const NUMERIC_STAT_LABELS: Record<string, string> = {
	average: 'Average',
	max: 'Maximum',
	minimum: 'Minimum',
}

function NumericStat({ aggregationType, value }: { aggregationType: string; value: number }) {
	const statLabel = NUMERIC_STAT_LABELS[aggregationType] ?? aggregationType
	const formatted = Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '')

	return (
		<div className='flex flex-col gap-1 px-4 py-4 bg-neutral-50 border border-neutral-200 rounded-md'>
			<Text variant='xsmall' className='text-neutral-500 text-left'>
				{statLabel}
			</Text>
			<Text variant='h4' weight='semibold' className='text-neutral-900 text-left'>
				{formatted}
			</Text>
		</div>
	)
}

const DEFAULT_OPTION_COLORS = ['#f87171', '#fb923c', '#fbbf24', '#a3e635', '#34d399', '#60a5fa', '#a78bfa', '#f472b6']

function OptionDistribution({ optionData }: { optionData: AggregationOptionData[] }) {
	const total = optionData[0]?.total ?? 0

	return (
		<div className='flex flex-col gap-3'>
			<Text variant='xsmall' className='text-neutral-500 text-left'>
				Distribution
			</Text>
			<div className='flex h-7 rounded-md overflow-hidden gap-px bg-neutral-100'>
				{optionData.map((opt, i) => {
					const pct = total > 0 ? (opt.count / total) * 100 : 0
					if (pct === 0) return null
					return (
						<div
							key={opt.value}
							style={{
								width: `${pct}%`,
								backgroundColor: opt.color ?? DEFAULT_OPTION_COLORS[i % DEFAULT_OPTION_COLORS.length],
							}}
						/>
					)
				})}
			</div>
			<div className='grid grid-cols-3 gap-x-6 gap-y-3'>
				{optionData.map((opt, i) => {
					const pct = total > 0 ? Math.round((opt.count / total) * 100) : 0
					return (
						<div key={opt.value} className='flex flex-col gap-0.5'>
							<div className='flex items-center gap-1.5'>
								<div
									className='shrink-0 w-2.5 h-2.5 rounded-full'
									style={{ backgroundColor: opt.color ?? DEFAULT_OPTION_COLORS[i % DEFAULT_OPTION_COLORS.length] }}
								/>
								<Text variant='xsmall' className='text-neutral-500 text-left'>
									{opt.label}
								</Text>
							</div>
							<Text variant='small' className='text-left'>
								<strong>{opt.count}</strong>/{total} ({pct}%)
							</Text>
						</div>
					)
				})}
			</div>
		</div>
	)
}

function OptionSingle({ optionData, mode }: { optionData: AggregationOptionData[]; mode: 'mostVoted' | 'leastVoted' }) {
	const sorted = [...optionData].sort((a, b) => (mode === 'mostVoted' ? b.count - a.count : a.count - b.count))
	const topCount = sorted[0]?.count
	if (topCount === undefined) return null

	const tied = sorted.filter((o) => o.count === topCount)

	return (
		<div className='flex flex-col gap-2'>
			{tied.map((opt) => {
				const total = opt.total
				const pct = total > 0 ? Math.round((opt.count / total) * 100) : 0
				const colorIndex = optionData.findIndex((o) => o.value === opt.value)
				const color = opt.color ?? DEFAULT_OPTION_COLORS[colorIndex % DEFAULT_OPTION_COLORS.length]

				return (
					<div
						key={opt.value}
						className='flex items-center gap-3 px-4 py-3 border border-neutral-200 rounded-md bg-white'>
						<div className='shrink-0 w-3 h-3 rounded-full' style={{ backgroundColor: color }} />
						<div className='flex flex-col gap-0.5'>
							<Text variant='small' weight='semibold' className='text-neutral-900 text-left'>
								{opt.label}
							</Text>
							<Text variant='xsmall' className='text-neutral-500 text-left'>
								{opt.count}/{total} ({pct}%)
							</Text>
						</div>
					</div>
				)
			})}
		</div>
	)
}

function ResponseItem({ response, renderAsHtml }: { response: string; renderAsHtml?: boolean }) {
	return (
		<div className='px-4 py-3 bg-white'>
			{renderAsHtml ? (
				<MarkdownPreview markdown={response} className='text-sm text-neutral-900 text-left' />
			) : (
				<Text variant='small' className='text-neutral-900 whitespace-pre-wrap'>
					{response}
				</Text>
			)}
		</div>
	)
}

const formatRole = (role: string): string =>
	role
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ')

function RoleSection({ role, children }: { role: string; children: import('react').ReactNode }) {
	return (
		<div className='flex flex-col gap-2'>
			<Text variant='small' weight='semibold' className='text-neutral-700'>
				{formatRole(role)}
			</Text>
			{children}
		</div>
	)
}

function RateGroupSection({ rateGroup, children }: { rateGroup: string; children: import('react').ReactNode }) {
	return (
		<div className='flex flex-col gap-1.5 pl-3 border-l-2 border-neutral-200'>
			<Text variant='xsmall' weight='regular' className='text-neutral-500'>
				{rateGroup}
			</Text>
			{children}
		</div>
	)
}

function TextResponseList({ responses, renderAsHtml }: { responses: string[]; renderAsHtml?: boolean }) {
	return (
		<div className='flex flex-col divide-y divide-neutral-200 border border-neutral-200 rounded-md overflow-hidden'>
			{responses.map((response, idx) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: responses are positional
				<ResponseItem key={idx} response={response} renderAsHtml={renderAsHtml} />
			))}
		</div>
	)
}

function RoleGroupContent({
	roleData,
	aggregationType,
	renderAsHtml,
}: {
	roleData: AggregationRoleData
	aggregationType: string
	renderAsHtml?: boolean
}) {
	if (roleData.responses !== undefined) {
		return <TextResponseList responses={roleData.responses} renderAsHtml={renderAsHtml} />
	}
	if (roleData.optionData !== undefined) {
		if (aggregationType === 'distribution') return <OptionDistribution optionData={roleData.optionData} />
		if (aggregationType === 'mostVoted') return <OptionSingle optionData={roleData.optionData} mode='mostVoted' />
		if (aggregationType === 'leastVoted') return <OptionSingle optionData={roleData.optionData} mode='leastVoted' />
	}
	if (roleData.numericValue !== undefined) {
		return <NumericStat aggregationType={aggregationType} value={roleData.numericValue} />
	}
	return null
}

export function FormsAggregation({
	component,
	data,
	optionData,
	numericValue,
	roles,
	loading,
	emptyPlaceholder,
	renderAsHtml,
}: FormsAggregationProps) {
	const { label, labelColor, labelSize, labelWeight, aggregationType } = component.custom as AggregationCustomProps

	const renderContent = () => {
		if (loading) {
			return (
				<div className='flex items-center gap-2 px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-md'>
					<div className='animate-spin inline-block size-3.5 border-2 border-neutral-400 border-t-transparent rounded-full' />
					<Text variant='small' className='text-neutral-400'>
						Loading responses...
					</Text>
				</div>
			)
		}

		// Grouped by role (with optional rate group nesting)
		if (roles && roles.length > 0) {
			const hasRateGroups = roles.some((r) => r.rateGroups && r.rateGroups.length > 0)
			return roles.map((roleData) => (
				<RoleSection key={roleData.role} role={roleData.role}>
					{hasRateGroups && roleData.rateGroups ? (
						roleData.rateGroups.map((rg) => (
							<RateGroupSection key={rg.rateGroup} rateGroup={rg.rateGroup}>
								<RoleGroupContent
									roleData={{ role: roleData.role, ...rg }}
									aggregationType={aggregationType}
									renderAsHtml={renderAsHtml}
								/>
							</RateGroupSection>
						))
					) : (
						<RoleGroupContent roleData={roleData} aggregationType={aggregationType} renderAsHtml={renderAsHtml} />
					)}
				</RoleSection>
			))
		}

		if (numericValue !== undefined) {
			return <NumericStat aggregationType={aggregationType} value={numericValue} />
		}

		if (optionData && optionData.length > 0) {
			if (aggregationType === 'distribution') return <OptionDistribution optionData={optionData} />
			if (aggregationType === 'mostVoted') return <OptionSingle optionData={optionData} mode='mostVoted' />
			if (aggregationType === 'leastVoted') return <OptionSingle optionData={optionData} mode='leastVoted' />
		}

		if (!data || data.length === 0) {
			return (
				<div className='px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-md text-center'>
					<Text variant='small' className='text-neutral-400'>
						{emptyPlaceholder ?? 'No responses yet.'}
					</Text>
				</div>
			)
		}

		if (aggregationType === 'combine') {
			const combined = data.flatMap((group) => group.responses)
			return (
				<div className='flex flex-col divide-y divide-neutral-200 border border-neutral-200 rounded-md overflow-hidden'>
					{combined.map((response, idx) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: responses are positional in a combined flat list
						<ResponseItem key={idx} response={response} renderAsHtml={renderAsHtml} />
					))}
				</div>
			)
		}

		return data.map((group) => (
			<div key={group.role} className='flex flex-col gap-2'>
				<Text variant='small' weight='regular' className='text-neutral-600'>
					{formatRole(group.role)}
				</Text>
				<div className='flex flex-col divide-y divide-neutral-200 border border-neutral-200 rounded-md overflow-hidden'>
					{group.responses.map((response, idx) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: responses are positional within a role group
						<ResponseItem key={idx} response={response} renderAsHtml={renderAsHtml} />
					))}
				</div>
			</div>
		))
	}

	return (
		<div className='flex flex-col gap-3'>
			{label && labelWeight !== 'hidden' && (
				<Text variant={getLabelVariant(labelSize, 'xsmall')} weight={labelWeight} style={{ color: labelColor }}>
					{label}
				</Text>
			)}
			{renderContent()}
		</div>
	)
}
